// Builds a clean, deduped list of *current, supported* ISO-4217 currencies.
// Uses `currency-codes` for names/digits/countries and filters with `Intl.NumberFormat`
// so we only show what the runtime can actually format (good for symbol/preview).
import cc from "currency-codes";

// Exclude known non-tender/test pseudo-currencies
const EXCLUDE = new Set(["XTS", "XXX", "XAU", "XAG", "XPT", "XPD"]);

let _cache = null;

function supportedByIntl(code) {
  try {
    // Will throw if the engine doesn't recognize the currency
    new Intl.NumberFormat(undefined, { style: "currency", currency: code }).format(1);
    return true;
  } catch {
    return false;
  }
}

function currencySymbol(code) {
  try {
    const nf = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    });
    const part = nf.formatToParts(1).find((p) => p.type === "currency");
    return part?.value || code;
  } catch {
    return code;
  }
}

function currencyExponent(code) {
  try {
    const nf = new Intl.NumberFormat(undefined, { style: "currency", currency: code });
    // Commonly matches ISO minor units; good enough for UI constraints & previews.
    return nf.resolvedOptions().maximumFractionDigits;
  } catch {
    return 2;
  }
}

function norm(s) {
  return (s || "")
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Returns array of:
 * { code, name, symbol, digits, countries, normKey }
 */
export function getCurrencies() {
  if (_cache) return _cache;

  // currency-codes can include some historical entries; `codes()` + `code()` gives us details
  const list = cc
    .codes()
    .filter((code) => !EXCLUDE.has(code))
    .filter(supportedByIntl)
    .map((code) => {
      const info = cc.code(code);
      const name = info?.currency || code;
      const countries = info?.countries || [];
      const digits = Number.isFinite(info?.digits) ? info.digits : currencyExponent(code);
      const symbol = currencySymbol(code);
      const searchText = [code, name, ...countries].join(" ");
      return {
        code,
        name,
        symbol,
        digits,
        countries,
        normKey: norm(searchText),
      };
    });

  // Sort by name (stable), with a small group of popular ones bubbled to top
  const popular = new Set(["EUR", "USD", "GBP", "JPY", "AUD", "CAD", "CHF", "CNY", "INR"]);
  const popularItems = [];
  const others = [];
  for (const c of list) (popular.has(c.code) ? popularItems : others).push(c);

  popularItems.sort((a, b) => a.name.localeCompare(b.name));
  others.sort((a, b) => a.name.localeCompare(b.name));

  _cache = [...popularItems, ...others];
  return _cache;
}

export function findCurrency(code) {
  return getCurrencies().find((c) => c.code === String(code).toUpperCase()) || null;
}

/**
 * Keep a tiny "recent" list to improve UX for global users.
 */
const RECENT_KEY = "recentCurrencies";
export function getRecentCurrencies() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return (Array.isArray(raw) ? raw : [])
      .map((code) => findCurrency(code))
      .filter(Boolean)
      .slice(0, 6);
  } catch {
    return [];
  }
}
export function pushRecentCurrency(code) {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    const next = [code, ...list.filter((c) => c !== code)].slice(0, 8);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore storage errors */
  }
}

/**
 * Helper to format a preview, e.g. "€12,345.67".
 */
export function formatCurrencyPreview(code, amount = 12345.67) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: code }).format(amount);
  } catch {
    return `${code} ${amount}`;
  }
}
